import React from 'react';

import classnames from 'classnames';
import { Radio } from '@bringg/react-components';
import { Driver } from '@bringg/types';
import { useTranslation } from 'react-i18next';

import styles from 'bringg-web/features/dispatch-page/employee-list/employee-list.module.scss';
import { truncateString } from 'bringg-web/utils/other';
import HomeNo from 'bringg-web/features/dispatch-page/employee-list/images/home_y_bt_n.png';
import HomeYes from 'bringg-web/features/dispatch-page/employee-list/images/home_y_bt_y.png';
import { useHasFeatureFlag, useStores } from 'bringg-web';
import { executeAction } from 'bringg-web/services/cross-application/cross-application';
import { EmployeeListInMapTab } from 'bringg-web/services/cross-application/cross-application.actions';

interface EmployeeRowProps {
	employee: Driver & { sending_home_event: boolean };
	selectedEmployeeId?: number;
	driverListFilterByOnShift: any;
}
const VEHICLE_NAME_MAX_LENGTH = 20;

const EmployeeRow = ({ employee, selectedEmployeeId, driverListFilterByOnShift }: EmployeeRowProps) => {
	const { t } = useTranslation();
	const showDriverRoleInDispatch = useHasFeatureFlag('show_driver_role_in_dispatch');
	const displayBeaconAtHomeIcons = useHasFeatureFlag('display_beacon_at_home_icons');
	const showVehicleName = useHasFeatureFlag('show_vehicle_name_in_employee_list');

	const markHomeEnabled1 = useHasFeatureFlag('markHomeFromMapEnabled');
	const markHomeEnabled2 = useHasFeatureFlag('feature_flag_only_dashboard_home_events');
	const markHomeEnabled = markHomeEnabled1 || markHomeEnabled2;

	const { authStore, vehiclesStore, userTypeStore } = useStores();
	const { crossAppTransport } = authStore;

	let userTypeTitle = '';
	let vehicleName = '';

	if (employee.user_type_id) {
		userTypeTitle = userTypeStore.get(employee.user_type_id)?.title;
	}

	if (employee.vehicle_id) {
		vehicleName = vehiclesStore.get(employee.vehicle_id)?.name || '';
	}

	function driverGotHome(driver: Driver) {
		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_DRIVER_GOT_HOME, driver);
	}

	function driverLeftHome(driver: Driver) {
		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_DRIVER_LEFT_HOME, driver);
	}

	function pickEmployee(driver: Driver) {
		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_PICK_EMPLOYEE, driver);
	}
	return (
		<div
			data-test-id={`employee-item-${employee.id}`}
			key={employee.id}
			className={classnames(styles.listItem, {
				[styles.selected]: employee.id === selectedEmployeeId
			})}
		>
			<div
				role="button"
				className={classnames(styles.employeeNameAndProfile, {
					[styles.withMargin]: !showVehicleName
				})}
				onClick={() => pickEmployee(employee)}
			>
				<div className={styles.employeeDetailsWrapper}>
					<img src={employee.profile_image} alt="Profile picture" className={classnames(styles.avatar)} />
					<a className={styles.employeeName}>{employee.name}</a>
				</div>
			</div>

			{employee.current_unavailability_id ? (
				<span className={styles.employeeExtraData}>{t('DISPATCH.DRIVER_UNAVAILABLE')}</span>
			) : null}

			{employee.is_on_break && !employee.current_unavailability_id ? (
				<span className={styles.employeeExtraData}>{t('MAP_USER_PROFILE.DRIVER_ON_BREAK')}</span>
			) : null}

			{showDriverRoleInDispatch && !employee.is_on_break ? (
				<span className={styles.employeeExtraData}>{userTypeTitle}</span>
			) : null}

			{showVehicleName && (
				<span className={styles.employeeVehicle}>{truncateString(vehicleName, VEHICLE_NAME_MAX_LENGTH)}</span>
			)}

			<div className={styles.employeeIconsBackground}>
				{displayBeaconAtHomeIcons && driverListFilterByOnShift() ? (
					<span className={styles.employeeBeaconHomeIconsWrapper}>
						<img
							src={HomeNo}
							className={classnames(styles.beaconHomeIcon, {
								[styles.hide]: !(employee.at_home && !employee.in_beacon_range)
							})}
						/>
						<img
							src={HomeYes}
							className={classnames(styles.beaconHomeIcon, {
								[styles.hide]: !(employee.at_home && employee.in_beacon_range)
							})}
						/>
					</span>
				) : null}

				{markHomeEnabled && driverListFilterByOnShift() ? (
					<Radio.Group
						disabled={employee.sending_home_event}
						value={employee.at_home ? 'home-in' : 'home-out'}
					>
						<Radio
							data-test-id={`employee-${employee.id}-in-radio`}
							type="radio"
							value="home-in"
							onChange={() => driverGotHome(employee)}
						>
							{t('MAP.HOME_IN')}
						</Radio>
						<Radio
							type="radio"
							data-test-id={`employee-${employee.id}-out-radio`}
							value="home-out"
							onChange={() => driverLeftHome(employee)}
						>
							{t('MAP.HOME_OUT')}
						</Radio>
					</Radio.Group>
				) : null}
			</div>
		</div>
	);
};

export default EmployeeRow;
